import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { 
    MapIcon, 
    MagnifyingGlassIcon,
    FunnelIcon,
    UserCircleIcon,
    XMarkIcon,
    PlusIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ChevronRightIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import FilterDropdown from '../common/FilterDropdown';
import Modal from '../common/Modal';
import { generateSOWPDF } from '../../utils/pdfGenerators';

const SOWTrackerTab = ({ isHOS = false }) => {
    const { get } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();
    
    // Core States
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    
    // Filter States
    const [selectedLearningArea, setSelectedLearningArea] = useState('all');
    const [selectedGradeStream, setSelectedGradeStream] = useState('all');
    const [selectedTerm, setSelectedTerm] = useState('');
    
    // Create Modal States
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ learning_area: '', academic_term: '' });
    const [isCreating, setIsCreating] = useState(false);
    const [exportingSowId, setExportingSowId] = useState(null);
    
    // Fetch Academic Terms
    const { data: terms } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
    });

    const activeTermId = selectedTerm || (terms?.find(t => t.is_current)?.id);

    // Fetch SOW Data
    // For HOS: fetch hos_teacher_sows to see all teachers
    // For Teachers: fetch their own SOWs
    const { data: sowsData, isLoading } = useQuery({
        queryKey: isHOS ? ['hos-teacher-sows', activeTermId] : ['teacher-sows', activeTermId],
        queryFn: () => get(isHOS 
            ? `/api/teacher/hos/teacher-sows/?term_id=${activeTermId || ''}` 
            : `/api/cbc/schemes-of-work/?term=${activeTermId || ''}`
        ),
        enabled: !!activeTermId
    });

    // Fetch Teacher's Learning Areas for the Create Modal
    const { data: myLearningAreas } = useQuery({
        queryKey: ['my-learning-areas'],
        queryFn: () => get('/api/cbc/learning-areas/'),
        enabled: !isHOS && showCreateModal
    });

    // Flatten data for display
    const displayList = useMemo(() => {
        if (!sowsData) return [];
        
        if (isHOS) {
            // HOS data is nested by teacher
            const list = [];
            sowsData.forEach(teacher => {
                teacher.learning_areas?.forEach(la => {
                    list.push({
                        id: la.sow_id || `no-sow-${teacher.id}-${la.id}`,
                        sow_id: la.sow_id,
                        teacher_name: teacher.name,
                        teacher_id: teacher.id,
                        learning_area_name: la.name,
                        learning_area_id: la.id,
                        grade_level_name: la.grade_level_name,
                        stream_name: la.stream_name,
                        has_sow: la.has_sow,
                        total_weeks: la.total_weeks || 0,
                        total_lessons: la.total_lessons || 0
                    });
                });
            });
            return list;
        } else {
            // Teacher data is a direct list of SchemeOfWork
            const rawList = sowsData.results || sowsData || [];
            return rawList.map(item => ({
                ...item,
                learning_area_name: item.learning_area?.name || item.learning_area_name || '',
                grade_level_name: item.learning_area?.grade_level_name || item.grade_level_name || '',
                stream_name: item.learning_area?.stream_name || item.stream_name || '',
                has_sow: true,
                total_weeks: item.total_weeks || 0,
                total_lessons: item.total_lessons || 0
            }));
        }
    }, [sowsData, isHOS]);

    // 1. Learning Area Filter Options (Pure subject names)
    const learningAreaOptions = useMemo(() => {
        const names = new Set();
        displayList.forEach(item => {
            const name = item.learning_area_name;
            if (name) names.add(name);
        });
        return [
            { name: 'All Learning Areas', value: 'all' },
            ...Array.from(names).sort().map(name => ({ name, value: name }))
        ];
    }, [displayList]);

    // 2. Grade & Stream Filter Options
    const gradeStreamOptions = useMemo(() => {
        const gradeMap = new Map();
        displayList.forEach(item => {
            const gName = item.grade_level_name;
            const sName = item.stream_name;
            if (gName) {
                if (!gradeMap.has(gName)) {
                    gradeMap.set(gName, new Set());
                }
                if (sName) {
                    gradeMap.get(gName).add(sName);
                }
            }
        });

        const list = [{ name: 'All Grades & Streams', value: 'all' }];
        Array.from(gradeMap.keys()).sort().forEach(gName => {
            list.push({ name: `${gName} (All Streams)`, value: `grade_${gName}` });
            const streams = Array.from(gradeMap.get(gName)).sort();
            streams.forEach(sName => {
                list.push({ name: `${gName} • Stream ${sName}`, value: `stream_${gName}_${sName}` });
            });
        });
        return list;
    }, [displayList]);

    // 3. Academic Term Options
    const termOptions = useMemo(() => [
        { name: 'All Academic Terms', value: 'all' },
        ...(terms || []).map(t => ({
            name: `${t.name} ${t.is_current ? '• (Current)' : ''}`.trim(),
            value: t.id.toString()
        }))
    ], [terms]);

    const hasActiveFilters = (selectedLearningArea !== 'all') || (selectedGradeStream !== 'all') || (selectedTerm !== '' && selectedTerm !== 'all');

    const handleResetFilters = () => {
        setSelectedLearningArea('all');
        setSelectedGradeStream('all');
        setSelectedTerm('');
        setSearchQuery('');
    };

    const filteredList = useMemo(() => {
        return displayList.filter(item => {
            const laName = item.learning_area_name || '';
            const gradeName = item.grade_level_name || '';
            const streamName = item.stream_name || '';
            const teacherName = (isHOS ? item.teacher_name : item.created_by_name) || '';

            // Search query match
            if (searchQuery.trim()) {
                const searchStr = `${laName} ${gradeName} ${streamName} ${teacherName}`.toLowerCase();
                if (!searchStr.includes(searchQuery.toLowerCase())) return false;
            }

            // Learning Area filter
            if (selectedLearningArea && selectedLearningArea !== 'all') {
                if (laName.toLowerCase() !== selectedLearningArea.toLowerCase()) return false;
            }

            // Grade & Stream filter
            if (selectedGradeStream && selectedGradeStream !== 'all') {
                if (selectedGradeStream.startsWith('stream_')) {
                    const parts = selectedGradeStream.replace('stream_', '').split('_');
                    const targetGrade = parts[0]?.toLowerCase();
                    const targetStream = parts.slice(1).join('_').toLowerCase();
                    if (targetGrade && gradeName.toLowerCase() !== targetGrade) return false;
                    if (targetStream && (!streamName || !streamName.toLowerCase().includes(targetStream))) return false;
                } else if (selectedGradeStream.startsWith('grade_')) {
                    const targetGrade = selectedGradeStream.replace('grade_', '').toLowerCase();
                    if (gradeName.toLowerCase() !== targetGrade) return false;
                }
            }

            return true;
        });
    }, [displayList, isHOS, searchQuery, selectedLearningArea, selectedGradeStream]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <i className="fas fa-spinner fa-spin text-2xl text-indigo-600"></i>
            </div>
        );
    }

    const handleOpenSow = (item) => {
        if (isHOS) {
            if (!item.sow_id) return;
            navigate(`/teacher/scheme-of-work/${item.learning_area_id}`, {
                state: {
                    learningAreaId: item.learning_area_id,
                    sowId: item.sow_id,
                    teacherId: item.teacher_id,
                    readOnly: true,
                    isHOS: true,
                    teacherName: item.teacher_name,
                    learningAreaName: item.learning_area_name,
                },
            });
        } else {
            const laId = item.learning_area?.id || item.learning_area;
            navigate(`/teacher/scheme-of-work/${laId}`, {
                state: { learningAreaId: laId },
            });
        }
    };

    const handleExportSOW = async (item) => {
        if (!item.sow_id || exportingSowId) return;
        setExportingSowId(item.sow_id);
        if (showToast) showToast('Preparing Scheme of Work PDF...', 'info');
        try {
            const data = await get(`/api/cbc/schemes-of-work/${item.sow_id}/`);
            await generateSOWPDF(data, data.weekly_breakdown);
            if (showToast) showToast('Scheme of Work PDF exported successfully!', 'success');
        } catch (error) {
            console.error('Failed to export SOW:', error);
            if (showToast) showToast(error?.response?.data?.error || 'Failed to export Scheme of Work.', 'error');
        } finally {
            setExportingSowId(null);
        }
    };

    const handleCreateSOW = async (e) => {
        e.preventDefault();
        if (!createForm.learning_area || !createForm.academic_term) return;
        
        try {
            setIsCreating(true);
            const res = await get.post('/api/cbc/schemes-of-work/teacher-sow/', {
                learning_area: createForm.learning_area,
                academic_term: createForm.academic_term,
                weekly_breakdown: []
            });
            
            setShowCreateModal(false);
            setCreateForm({ learning_area: '', academic_term: '' });
            
            // Navigate to the newly created SOW
            navigate(`/teacher/scheme-of-work/${createForm.learning_area}`, {
                state: { learningAreaId: createForm.learning_area },
            });
        } catch (error) {
            console.error('Failed to create SOW:', error);
            if (showToast) showToast(error?.response?.data?.error || 'Failed to create Scheme of Work. Please try again.', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
                            <MapIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                {isHOS ? 'School SOW Tracker' : 'My Schemes of Work'}
                            </h2>
                            <p className="text-slate-500 font-medium text-xs mt-1">
                                {isHOS ? 'Monitor schemes of work across all teachers.' : 'Manage your termly teaching roadmaps.'}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative group flex-1 sm:w-80 max-w-xs">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <MagnifyingGlassIcon className="w-5 h-5" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search learning areas..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-sm font-bold text-[#18216D]"
                        />
                    </div>
                    {!isHOS && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="p-2 md:px-4 md:py-2 bg-[#18216D] text-white rounded-xl shadow-sm hover:bg-indigo-800 transition-all flex items-center gap-2 border border-transparent"
                        >
                            <PlusIcon className="w-5 h-5" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Create</span>
                        </button>
                    )}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`p-2 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${
                            showFilters 
                                ? 'bg-[#18216D] text-white border-transparent' 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        <FunnelIcon className="w-5 h-5" />
                        <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Filters</span>
                    </button>
                    <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-sm">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="Grid View"
                        >
                            <Squares2X2Icon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="List View"
                        >
                            <ListBulletIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Pop-Out Filters Drawer (Matching Classes & Grading Pattern) */}
            {showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Schemes of Work
                            </span>
                            {hasActiveFilters && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* 1. Learning Area Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Learning Area
                            </label>
                            <FilterDropdown
                                value={selectedLearningArea}
                                onChange={(val) => setSelectedLearningArea(val || 'all')}
                                options={learningAreaOptions}
                                placeholder="All Learning Areas"
                                className="w-full"
                            />
                        </div>

                        {/* 2. Grade & Stream Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Grade &amp; Stream
                            </label>
                            <FilterDropdown
                                value={selectedGradeStream}
                                onChange={(val) => setSelectedGradeStream(val || 'all')}
                                options={gradeStreamOptions}
                                placeholder="All Grades &amp; Streams"
                                className="w-full"
                            />
                        </div>

                        {/* 3. Academic Term Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Term
                            </label>
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val || '')}
                                options={termOptions}
                                placeholder={terms?.find(t => t.is_current)?.name || "Current Term"}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            )}

            {filteredList.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <MapIcon className="w-10 h-10 text-slate-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#18216D] mb-2">No Schemes of Work</h3>
                    <p className="text-slate-400 font-medium max-w-md mx-auto">
                        No schemes of work match your current filters.
                    </p>
                </div>
            ) : viewMode === 'list' ? (
                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    <th className="p-4 pl-6">Learning Area</th>
                                    <th className="p-4">Grade & Stream</th>
                                    {isHOS && <th className="p-4">Teacher</th>}
                                    <th className="p-4">Status</th>
                                    <th className="p-4">Breakdown</th>
                                    <th className="p-4 pr-6 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((item) => (
                                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 pl-6">
                                            <div className="font-bold text-[#18216D]">
                                                {isHOS ? item.learning_area_name : item.learning_area?.name}
                                            </div>
                                            {!isHOS && item.title && (
                                                <div className="text-xs text-slate-500 font-medium line-clamp-1 mt-0.5">
                                                    {item.title}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                                                {isHOS ? item.grade_level_name : item.learning_area?.grade_level_name}
                                                {(isHOS ? item.stream_name : item.learning_area?.stream_name) ? ` - ${(isHOS ? item.stream_name : item.learning_area?.stream_name)}` : ''}
                                            </span>
                                        </td>
                                        {isHOS && (
                                            <td className="p-4 text-xs font-semibold text-slate-600 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <UserCircleIcon className="w-4 h-4 text-[#18216D]" />
                                                    {item.teacher_name}
                                                </div>
                                            </td>
                                        )}
                                        <td className="p-4 whitespace-nowrap">
                                            {!isHOS && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                                                    Active SOW
                                                </div>
                                            )}
                                            {isHOS && item.has_sow && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                                                    Created
                                                </div>
                                            )}
                                            {isHOS && !item.has_sow && (
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-amber-100">
                                                    Missing
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 whitespace-nowrap">
                                            <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-black uppercase tracking-widest">
                                                {item.has_sow ? `${item.total_weeks || 0}W • ${item.total_lessons || 0}L` : '-'}
                                            </span>
                                        </td>
                                        <td className="p-4 pr-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenSow(item)}
                                                    disabled={isHOS && !item.has_sow}
                                                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                                                        isHOS && !item.has_sow 
                                                            ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                                                            : 'bg-[#18216D] text-white hover:bg-indigo-700 shadow-sm hover:shadow'
                                                    }`}
                                                >
                                                    View Scheme
                                                </button>
                                                {item.has_sow && (
                                                    <button
                                                        onClick={() => handleExportSOW(item)}
                                                        disabled={exportingSowId === item.sow_id}
                                                        className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-wait rounded-xl transition-all border border-transparent shadow-sm flex-none"
                                                        title="Download PDF"
                                                    >
                                                        {exportingSowId === item.sow_id ? (
                                                            <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <ArrowDownTrayIcon className="w-4 h-4 stroke-[2]" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredList.map((item) => (
                        <div key={item.id} className="bg-white rounded-[2.5rem] border border-slate-100 p-8 hover:shadow-[0_25px_60px_-15px_rgba(24,33,109,0.12)] hover:border-indigo-100 transition-all group flex flex-col h-full relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/30 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-125"></div>
                            <div className={`absolute top-4 left-8 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${
                                !isHOS ? 'bg-emerald-50 text-emerald-600' :
                                item.has_sow ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                            }`}>
                                {!isHOS ? 'Active SOW' : item.has_sow ? 'Created' : 'Missing'}
                            </div>

                            <div className="mt-12 relative z-10"></div>

                            <div className="flex-1 relative z-10">
                                <h3 className="text-xl font-black text-[#18216D] line-clamp-2 mb-5 tracking-tighter leading-tight italic">
                                    {isHOS ? item.learning_area_name : item.learning_area?.name}
                                </h3>
                                
                                <div className="flex flex-col gap-3 mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.15em] italic">
                                                {isHOS ? item.grade_level_name : item.learning_area?.grade_level_name}
                                                {(isHOS ? item.stream_name : item.learning_area?.stream_name) ? ` (${(isHOS ? item.stream_name : item.learning_area?.stream_name)})` : ''}
                                            </span>
                                        </div>
                                    </div>

                                    {isHOS && item.teacher_name && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                            <span className="text-[10px] font-black text-amber-600 uppercase tracking-[0.15em] italic truncate">
                                                {item.teacher_name}
                                            </span>
                                        </div>
                                    )}

                                    {/* Week & Lesson Breakdown Count Badge */}
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                        <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100/70">
                                            {item.has_sow ? `${item.total_weeks || 0} Weeks • ${item.total_lessons || 0} Lessons` : 'No SOW Breakdown'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-slate-50 relative z-10 flex gap-2">
                                <button
                                    onClick={() => handleOpenSow(item)}
                                    disabled={isHOS && !item.has_sow}
                                    className={`flex-1 flex items-center justify-center gap-3 py-3 rounded-xl text-xs font-black transition-colors uppercase tracking-[0.2em] ${
                                        isHOS && !item.has_sow 
                                            ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                                            : 'bg-indigo-50 hover:bg-indigo-100 text-[#18216D]'
                                    }`}
                                >
                                    View Scheme
                                    {!(isHOS && !item.has_sow) && <ChevronRightIcon className="w-5 h-5 stroke-[3]" />}
                                </button>
                                {item.has_sow && (
                                    <button
                                        onClick={() => handleExportSOW(item)}
                                        disabled={exportingSowId === item.sow_id}
                                        className="p-3 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-wait rounded-xl transition-all border border-transparent shadow-sm flex-none"
                                        title="Download PDF"
                                    >
                                        {exportingSowId === item.sow_id ? (
                                            <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <ArrowDownTrayIcon className="w-5 h-5 stroke-[2]" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                isOpen={showCreateModal}
                onClose={() => !isCreating && setShowCreateModal(false)}
                title="Create Scheme of Work"
                size="md"
            >
                <form onSubmit={handleCreateSOW} className="p-6 space-y-5">
                    <div>
                        <label className="block text-xs font-black text-[#18216D] uppercase tracking-widest mb-2">
                            Learning Area
                        </label>
                        <select
                            required
                            value={createForm.learning_area}
                            onChange={(e) => setCreateForm({ ...createForm, learning_area: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold text-slate-700 transition-all"
                            disabled={isCreating}
                        >
                            <option value="">Select a learning area</option>
                            {myLearningAreas?.map(la => (
                                <option key={la.id} value={la.id}>
                                    {la.name} - {la.grade_level_name} {la.stream_name ? `(${la.stream_name})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-black text-[#18216D] uppercase tracking-widest mb-2">
                            Academic Term
                        </label>
                        <select
                            required
                            value={createForm.academic_term}
                            onChange={(e) => setCreateForm({ ...createForm, academic_term: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold text-slate-700 transition-all"
                            disabled={isCreating}
                        >
                            <option value="">Select academic term</option>
                            {terms?.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            disabled={isCreating}
                            className="px-6 py-2.5 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || !createForm.learning_area || !createForm.academic_term}
                            className="px-8 py-2.5 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-[0.15em] shadow-lg shadow-indigo-200 hover:bg-indigo-800 transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {isCreating ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <PlusIcon className="w-4 h-4" />
                                    Create SOW
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default SOWTrackerTab;
